import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { customerAPI, getApiErrorMessage } from '@matix/api-client';
import type { CustomerAddress, AddressInput } from '@matix/types';
import { useLang } from '../../lib/language';
import { AddressFields, hasAnyAddressField, stripEmpty } from './CustomerPicker';

/**
 * AddressPicker — radio list of the customer's non-deleted addresses with
 * inline "Add new address" form.
 *
 * Spec section 5.4. Each line is rendered with the area + structural fields
 * matching the screenshot. Empty fields drop their label rather than show
 * "—". First address is pre-selected by default (driven by the caller via
 * the `selectedId` prop).
 *
 * Picking one is OPTIONAL: a delivery order books for a customer with no
 * address at all, so the empty state offers the form rather than demanding it.
 */
export function AddressPicker(props: {
  customerId: number;
  addresses: CustomerAddress[];
  selectedId: number | null;
  /** The whole row, so the caller can read its delivery fee (a just-added
   *  address is not in `addresses` until the detail query refetches). */
  onSelect: (address: CustomerAddress) => void;
}) {
  const { t } = useLang();
  const [addOpen, setAddOpen] = useState(false);
  const [newAddress, setNewAddress] = useState<AddressInput>({});
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const add = useMutation({
    mutationFn: () => customerAPI.addAddress(props.customerId, stripEmpty(newAddress)),
    onSuccess: async (created) => {
      // Invalidate the customer-detail cache so the next read returns the
      // full updated list. Then auto-select the new row.
      await queryClient.invalidateQueries({ queryKey: ['customer-detail', props.customerId] });
      props.onSelect(created);
      setNewAddress({});
      setAddOpen(false);
    },
    onError: (err) => setError(getApiErrorMessage(err) || t({ en: 'Failed to add address', ar: 'تعذّر إضافة العنوان' })),
  });

  if (props.addresses.length === 0 && !addOpen) {
    return (
      <div className="address-picker empty">
        <div className="empty-msg">
          {/* The address is NOT required to book the order — say so, or the
              cashier keeps hunting for the field that is blocking CREATE. */}
          {t({
            en: 'This customer has no address. You can add one, or create the order without it.',
            ar: 'لا يوجد عنوان لهذا العميل. يمكنك إضافة عنوان أو إنشاء الطلب بدونه.',
          })}
        </div>
        <button
          type="button"
          className="link-btn"
          onClick={() => setAddOpen(true)}
        >
          {t({ en: '+ Add new address', ar: '+ إضافة عنوان جديد' })}
        </button>
      </div>
    );
  }

  return (
    <div className="address-picker">
      <ul className="address-list" role="radiogroup">
        {props.addresses.map((a) => (
          <li
            key={a.id}
            className={`address-row ${a.id === props.selectedId ? 'selected' : ''}`}
          >
            <label>
              <input
                type="radio"
                name="delivery-address"
                checked={a.id === props.selectedId}
                onChange={() => props.onSelect(a)}
              />
              <span className="address-summary">
                {/* City is only set by the Saudi National Address layout, so
                    a KSA address reads المدينة → الحي → الشارع like the SPL
                    card, while a legacy Gulf address is unchanged. */}
                {a.governorate_name && (
                  <span>{t({ en: 'City', ar: 'المدينة' })}: {a.governorate_name.trim()}</span>
                )}
                {a.area_name && (
                  <span>
                    {a.governorate_name ? t({ en: 'District', ar: 'الحي' }) : t({ en: 'Area', ar: 'المنطقة' })}
                    : {a.area_name.trim()}
                  </span>
                )}
                {a.avenue && <span>{t({ en: 'Avenue', ar: 'الجادة' })}: {a.avenue}</span>}
                {a.block && <span>{t({ en: 'Block', ar: 'القطعة' })}: {a.block}</span>}
                {a.street && <span>{t({ en: 'Street', ar: 'الشارع' })}: {a.street}</span>}
                {a.house && (
                  <span>
                    {a.governorate_name ? t({ en: 'Building', ar: 'رقم المبنى' }) : t({ en: 'House', ar: 'المنزل' })}
                    : {a.house}
                  </span>
                )}
                {a.secondary_number && (
                  <span>{t({ en: 'Additional no.', ar: 'الرقم الفرعي' })}: {a.secondary_number}</span>
                )}
                {a.postal_code && (
                  <span>{t({ en: 'Postal code', ar: 'الرمز البريدي' })}: {a.postal_code}</span>
                )}
                {a.short_address_code && (
                  <span>{t({ en: 'Short address', ar: 'العنوان المختصر' })}: {a.short_address_code}</span>
                )}
                {a.floor && <span>{t({ en: 'Floor', ar: 'الطابق' })}: {a.floor}</span>}
                {a.flat && <span>{t({ en: 'Flat', ar: 'الشقة' })}: {a.flat}</span>}
                {a.address_note && (
                  <span className="address-note">
                    {t({ en: 'Address Note', ar: 'ملاحظة العنوان' })}: {a.address_note}
                  </span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>

      {!addOpen ? (
        <button
          type="button"
          className="link-btn"
          onClick={() => setAddOpen(true)}
        >
          {t({ en: '+ Add new address', ar: '+ إضافة عنوان جديد' })}
        </button>
      ) : (
        <div className="add-address-form">
          <AddressFields value={newAddress} onChange={setNewAddress} />
          {error && <div className="form-error">{error}</div>}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setAddOpen(false);
                setNewAddress({});
                setError(null);
              }}
            >
              {t({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!hasAnyAddressField(newAddress) || add.isPending}
              onClick={() => {
                setError(null);
                add.mutate();
              }}
            >
              {add.isPending ? t({ en: 'Saving…', ar: 'جارٍ الحفظ…' }) : t({ en: 'Add address', ar: 'إضافة عنوان' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
